import { observable } from 'mobx';
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { startCase as _startCase, isObject as _isObject, keys as _keys } from 'lodash';
import { GCP_ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import { Flex, Text, Spinner, Button, CopyText } from 'core/components';
import { makeConfig } from 'app/views/hybrid/maps/components/popovers/configHelper';

import Collection from 'core/model/Collection';
import AdminTable from 'app/components/admin/AdminTable';
import SidebarDetails from 'app/views/hybrid/maps/components/popovers/SidebarDetails';
import ToolResultCard from '../../components/ToolResultCard';

// Entity types whose raw data is too complex or deeply nested to render usefully in the detailed view
const EXCLUDED_ENTITY_TYPES = [GCP_ENTITY_TYPES.INSTANCE, GCP_ENTITY_TYPES.PROJECT];

@inject('$hybridMap')
@observer
class GcpInventoryToolResult extends Component {
  @observable
  collection = new Collection();

  state = {
    config: null,
    showDetailedView: false,
    isFinishedLoading: false
  };

  get topology() {
    const { $hybridMap } = this.props;
    return $hybridMap.gcpCloudMapCollection.initialTopology;
  }

  componentDidMount() {
    const { $hybridMap, tool } = this.props;
    const { raw_result: results = [] } = tool;

    this.collection.set(results);

    $hybridMap.gcpCloudMapCollection.fetch().then(() => {
      this.setState({ isFinishedLoading: true });
    });
  }

  handleClickDetailedView = (entity) => {
    const { $hybridMap, tool } = this.props;
    const { query_object } = tool;
    const { entity_type } = query_object;

    const entityId = entity.get('selfLink');

    const resultEntity = $hybridMap.gcpCloudMapCollection.getEntity({ entityId, entityType: entity_type });

    const config = makeConfig({
      cloudProvider: 'gcp',
      type: entity_type,
      value: resultEntity.selfLink || resultEntity.id,
      nodeData: resultEntity,
      topology: this.topology,
      details: {
        panelTitle: 'Entity Details Summary',
        node: resultEntity,
        keys: Object.keys(resultEntity)
      }
    });

    this.setState({ config, showDetailedView: true });
  };

  handleCloseDetailedView = () => {
    this.setState({ showDetailedView: false, config: null });
  };

  get columns() {
    const { tool } = this.props;
    const { raw_result: results = [], query_object } = tool;
    const { entity_type } = query_object || {};

    const allKeys = results.reduce((acc, item) => {
      acc = [...new Set([...acc, ..._keys(item)])];
      return acc;
    }, []);

    const columns = allKeys
      .filter((key) => !_isObject(results[0]?.[key]))
      .slice(0, 8)
      .map((key) => ({
        label: _startCase(key),
        name: key,
        renderer: ({ value }) => {
          if (key.toLowerCase().includes('id') || key === 'selfLink') {
            return <CopyText text={value} />;
          }
          return <Text>{String(value || '')}</Text>;
        }
      }));

    if (entity_type && !EXCLUDED_ENTITY_TYPES.includes(entity_type)) {
      columns.push({
        name: 'action',
        label: 'Action',
        renderer: ({ model }) => (
          <Button text="Detailed View" small onClick={() => this.handleClickDetailedView(model)} />
        )
      });
    }

    return columns;
  }

  render() {
    const { tool, onCloseToolResult, isInlineMode } = this.props;
    const { config, showDetailedView, isFinishedLoading } = this.state;

    if (!isFinishedLoading) {
      return (
        <Flex p={2} flexDirection="row" gap="2" alignItems="center" justifyContent="center">
          <Spinner size={24} />
          <Text as="div">Loading GCP Cloud Topology...</Text>
        </Flex>
      );
    }

    return (
      <ToolResultCard
        tool={tool}
        onCloseToolResult={onCloseToolResult}
        isInlineMode={isInlineMode}
        className="tool-result-gcp-inventory"
        title={tool.title || 'GCP Inventory Tool Result'}
        headerActions={
          showDetailedView && (
            <Button
              text="Back"
              small
              icon="circle-arrow-left"
              onClick={this.handleCloseDetailedView}
              color="muted"
              minimal
            />
          )
        }
      >
        <Flex flexDirection="column" px={1} pb={1} flex={1}>
          {!showDetailedView && <AdminTable collection={this.collection} columns={this.columns} />}
          {showDetailedView && config && <SidebarDetails {...config} />}
        </Flex>
      </ToolResultCard>
    );
  }
}

export default GcpInventoryToolResult;
